import type {MutableRefObject, LegacyRef} from 'react';

import type {NonEmptyArray} from '@famly/stat_ts-utils_non-empty-array';

/**
 * Merges multiple refs into a single callback ref.
 *
 * @param refs Refs that should all receive the same instance.
 * @returns A callback ref that updates every provided ref.
 *
 * @example
 * const Foo = React.forwardRef<HTMLDivElement>((props, ref) => {
 *     const localRef = React.useRef<HTMLDivElement | null>(null);
 *
 *     return <div ref={mergeRefs(localRef, ref)} />;
 * });
 */
export const mergeRefs = <T = Element>(...refs: NonEmptyArray<MutableRefObject<T | null> | LegacyRef<T>>) => {
    return (value: T | null) => {
        refs.forEach(ref => {
            if (typeof ref === 'function') {
                ref(value);
            } else if (ref !== null) {
                // `LegacyRef` widens to `string | RefObject` whose `current` is read-only in the
                // types, but React mutates it at runtime — cast to the mutable shape to assign it.
                (ref as MutableRefObject<T | null>).current = value;
            }
        });
    };
};
